<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useShapeLayer } from 'shared/map/layers/shapeLayer';
import { useVehiclesLayer } from 'shared/map/layers/vehicleLayer';
import { usePlacesLayer } from 'shared/map/layers/placesLayer';
import { usePackagesLayer } from 'shared/map/layers/packagesLayer';
import MainLayout from '@/common/components/MainLayout.vue';
import ShapeDrawer from './components/ShapeDrawer.vue';
import { Assert } from 'shared/assert';
import { ContextMenu, Dialog, Select, Splitter, SplitterPanel, ToggleButton, type SplitterResizeEndEvent } from 'primevue';
import type { MenuItem } from 'primevue/menuitem';
import type { Coordinate } from 'shared/map/domain/coordinate';
import { getRenderedEventCoordinate } from 'shared/map/domain/coordinate';
import { useSelectedShapeStore } from './stores/selectedShapeStore';
import LogYardEditContentDialog from '@/storageAreas/logYards/components/LogYardEditContentDialog.vue';
import LogSorterDrawer from './components/LogSorterDrawer.vue';
import { useSelectedLogSorterStore, type EditLogSorter } from './stores/selectedLogSorterStore';
import { useSelectedPackageStorageStore } from './stores/selectedPackageStorageStore';
import { usePlacesEditInteraction } from './composables/placesEditInteraction';
import type { StoragePlace } from 'shared/storageAreas/domain/storageArea';
import { isBulkStorage, isCoilStorage, isLogYard, StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import MapStyleSelect from '@/mapStyles/components/MapStyleSelect.vue';
import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import UnsavedChangesDialog from 'shared/common/components/UnsavedChangesDialog.vue';
import { useShapeAuditApi } from '@/audit/api/shapeAuditApi';
import AuditDialog from 'shared/audit/components/AuditDialog.vue';
import { useVehicleAuditApi } from '@/audit/api/vehicleAuditApi';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useNewPosition } from './composables/newPosition';
import MoveCoilDialog from '@/coils/components/MoveCoilDialog.vue';
import EditPackageDialog from '@/packages/components/EditPackageDialog.vue';
import { useRefreshPackageStore } from '@/packages/commands/refreshPackageStore';
import { useRefreshPackageTypeStore } from '@/packages/commands/refreshPackageTypeStore';
import { useRefereshProductGroupStore } from '@/products/groups/commands/refreshProductGroupStore';
import PackageDetailsDrawer from 'shared/packages/components/PackageDetailsDrawer.vue';
import { usePackageDetailsStore } from 'shared/packages/stores/packageDetailsStore';
import { useSelectedStorageAreaStore } from './stores/selectedStorageAreaStore';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import MoveLogYardContentDialog from '@/storageAreas/logYards/components/MoveLogYardContentDialog.vue';
import Button from 'primevue/button';
import { useAuthStore } from '@/auth/store/authStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import SearchLogYardDialog from 'shared/search/components/SearchLogYardDialog.vue';
import { useSearchProduct } from 'shared/search/composables/searchProduct';
import { useRefreshSettingStore } from '@/settings/stores/refreshSettingStore';
import { useRefreshVehicleStore } from '@/vehicles/stores/refreshVehicleStore';
import MapView, { type MapApi, type MapSettingOptions, type BaseLayerType } from './components/MapView.vue';
import type { LogSorterResponse } from '@/logSorter/api/common/logSorterResponse';
import { useGetLogSorters } from '@/logSorter/api/getLogSorters';
import { useMoveTimberFromLogSorterBinsApi } from '@/moves/api/moveTimberFromLogSorterBinsApi';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';
import OLMap from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import DragBox from 'ol/interaction/DragBox.js';
import { platformModifierKeyOnly } from 'ol/events/condition.js';
import { getWidth } from 'ol/extent.js';
import { transformExtent } from 'ol/proj';
import { useGeographic } from 'ol/proj';
import type Feature from 'ol/Feature';
import type { Geometry } from 'ol/geom';
import { getShapeId, getGeoJsonPropertyText, getStorageAreaId } from 'shared/shapes/domain/shapeFeature';
import { usePreviewRectangleLayer } from 'shared/map/layers/previewRectangleLayer';
import { usePreviewPolygonLayer } from 'shared/map/layers/previewPolygonLayer';
import { usePreviewLogsorterLayer } from './layers/previewLogSorterLayer';
import BulkEditContentDialog from '@/storageAreas/bulk/components/BulkEditContentDialog.vue';
import MoveBulkContentDialog from '@/storageAreas/bulk/components/MoveBulkContentDialog.vue';
import BaseLayerSelect from './components/BaseLayerSelect.vue';
import MapTable from './components/MapTable.vue';
import { useShapeStore } from 'shared/shapes/stores/shapeStore.ts';
import type { StorageArea } from 'shared/storageAreas/domain/storageArea.ts';
import { center as turfCenter, type AllGeoJSON } from '@turf/turf';
import { useDarkMode } from 'shared/themes/composables/darkMode.ts';
import type VectorSource from 'ol/source/Vector';
import MapRotationControls from './components/MapRotationControls.vue';
import MapSideDrawer from './components/MapSideDrawer.vue';
import MultiPoint from 'ol/geom/MultiPoint';
import { useLocalStorage } from '@vueuse/core';
import AddInboundDeliveryTransactionDialog from '@/deliveries/components/AddInboundDeliveryTransactionDialog.vue';
import AddOutboundDeliveryTransactionDialog from '@/deliveries/components/AddOutboundDeliveryTransactionDialog.vue';
import { useRefreshState } from '@/state/composables/refreshState.ts';
import { unByKey } from 'ol/Observable';
import type { EventsKey } from 'ol/events';
import SearchFilterButton from 'shared/common/components/FilterProductsInMapButton.vue'
import UndoButton from 'shared/undo/components/UndoButton.vue';

useGeographic();

const shapeLayer = useShapeLayer();
const shapeStore = useShapeStore();
const vehiclesLayer = useVehiclesLayer();
const selectedPackageStorageStore = useSelectedPackageStorageStore();
const packageEditAreaStore = useSelectedStorageAreaStore();
const { selectedPlaceIds: selectedStoragePlaceIds, operation: packageStorageOperation } = storeToRefs(selectedPackageStorageStore);
const editingPackageStorageAreaId = computed(() =>
  packageStorageOperation.value === 'none' ? undefined : packageEditAreaStore.currentStorageArea?.id,
);
// Context for an area being created or copied that is not yet saved, so its
// (copied) places are rendered live against the shape before it exists in the store.
const editingPackageArea = computed(() =>
  packageStorageOperation.value === 'none'
    ? undefined
    : {
      id: packageEditAreaStore.currentStorageArea?.id ?? '__editing_package_area__',
      name: packageEditAreaStore.name,
      status: packageEditAreaStore.status,
    },
);
const editingStoragePlaces = computed<StoragePlace[]>(() => selectedPackageStorageStore.places as StoragePlace[]);
const selectedShapeStore = useSelectedShapeStore();
// Live geometry of the area being edited, so places follow it while moving/rotating.
const editingShapeFeature = computed<GeoJSON.Feature | undefined>(() =>
  packageStorageOperation.value === 'none' ? undefined : selectedShapeStore.geoJson?.shape,
);
const placesLayer = usePlacesLayer({
  selectedPlaceIds: selectedStoragePlaceIds,
  editingAreaId: editingPackageStorageAreaId,
  editingArea: editingPackageArea,
  editingPlaces: editingStoragePlaces,
  editingShapeFeature,
});
const packagesLayer = usePackagesLayer({
  clickEnabled: () => selectedShapeStore.selectionEnabled && packageStorageOperation.value === 'none',
});
const packageDetailsStore = usePackageDetailsStore();
packagesLayer.addClickListener(onPackageClick);
const placesEditInteraction = usePlacesEditInteraction();
const selectedLogSorterStore = useSelectedLogSorterStore();
const previewLogsorterLayer = usePreviewLogsorterLayer();
const refreshProductStore = useRefereshProductStore();
const actionFailedAlert = useActionFailedAlert();
const shapeAuditApi = useShapeAuditApi();
const vehicleAuditApi = useVehicleAuditApi();
const storageAreaStore = useStorageAreaStore();
const authStore = useAuthStore();
const selectedStorageAreaStore = useSelectedStorageAreaStore();
const { currentStorageArea: selectedStorageArea } = storeToRefs(selectedStorageAreaStore);
const settingStore = useSettingStore();
const refreshSettingStore = useRefreshSettingStore();
const refreshVehicleStore = useRefreshVehicleStore();
const getLogSorters = useGetLogSorters();
const moveTimberFromLogSorterBinsApi = useMoveTimberFromLogSorterBinsApi();
const featureFlags = useFeatureFlagStore();
const darkMode = useDarkMode();
const { enabled, mapColorMode } = storeToRefs(darkMode);

const currentBaseLayer = ref<BaseLayerType>('osm');

let map: OLMap | undefined;
let disposed = false;
const olKeys: EventsKey[] = [];        // ol .on(...) keys
const cleanups: Array<() => void> = []; // everything else
let animationTimers: number[] = [];

const mapDarkMode = computed(() => {
  if (currentBaseLayer.value === 'satellite') return false;
  if (mapColorMode.value === 'auto') {
    return enabled.value;
  }
  return mapColorMode.value === 'dark';
});

const { activeTab, selectionEnabled, selectedShapeIds } = storeToRefs(selectedShapeStore);
const { type: selectedStorageAreaType } = storeToRefs(selectedStorageAreaStore);

const previewRectangleLayer = usePreviewRectangleLayer();
const previewPolygonLayer = usePreviewPolygonLayer({
  selectedShapeIds,
  selectionEnabled
});

function updatePreviewLayers() {
  const previewActive = activeTab.value === 'shape' &&
    selectedStorageAreaType.value !== StorageAreaType.LogSorterBin;
  previewRectangleLayer.setActive(previewActive);
  previewPolygonLayer.setActive(previewActive);
}

watch([activeTab, selectedStorageAreaType], updatePreviewLayers, { immediate: true });

const newPosition = useNewPosition();

const rootElementRef = useTemplateRef('map-view');
const unsavedChangesDialog = useTemplateRef('unsaved-changes');
const shapeDrawer = useTemplateRef('shape-drawer');
const auditDialog = useTemplateRef('audit-dialog');
const moveCoilDialog = useTemplateRef('move-coil-dialog');
const editPackageDialog = useTemplateRef('edit-package-dialog');
const refreshPackageStore = useRefreshPackageStore();
const refreshPackageTypeStore = useRefreshPackageTypeStore();
const refreshProductGroupStore = useRefereshProductGroupStore();
onMounted(() => {
  if (!featureFlags.isPackagesEnabled()) return;
  refreshPackageTypeStore.initialize();
  refreshProductGroupStore.initialize();
});
const moveLogYardContentDialog = useTemplateRef('move-log-yard-content-dialog');
const moveBulkContentDialog = useTemplateRef('move-bulk-content-dialog');
const searchLogYardDialogRef = useTemplateRef('search-log-yard-dialog');
const mapTableSplitterRef = useTemplateRef('mapTableSplitter');
const sideDrawerRef = useTemplateRef('side-drawer');
const addInboundDeliveryTransactionDialog = useTemplateRef('add-inbound-delivery-transaction-dialog');
const addOutboundDeliveryTransactionDialog = useTemplateRef('add-outbound-delivery-transaction-dialog');
const animationSpeed = 350;
const isLogSorterPanelOpen = computed(() => selectedLogSorterStore.operation !== 'none');
const isSideDrawerOpen = computed(() => selectedShapeStore.showUI || isLogSorterPanelOpen.value);
const sideDrawerWidth = useLocalStorage('map-side-drawer-width', 400);

const mapTableExpanded = ref(false);
const isMapTableAnimating = ref(false)

const mapTableBtnVisible = ref(true);

const topPanelSize = ref(100)
const bottomPanelSize = ref(0)
const mapTableGutterSize = ref(0);

const mapApi = ref<MapApi>();

watch(mapTableExpanded, async (expanded) => {
  mapTableBtnVisible.value = !expanded;
  await nextTick();

  if (expanded) {
    topPanelSize.value = 60;
    bottomPanelSize.value = 40;
    mapTableGutterSize.value = 4;
  } else {
    topPanelSize.value = 100;
    bottomPanelSize.value = 0;
    mapTableGutterSize.value = 0;
  }

  isMapTableAnimating.value = true
  mapTableSplitterRef.value?.resetState();

  setTimeout(() => {
    isMapTableAnimating.value = false
    mapApi.value?.updateSize();
    // mapTableBtnVisible.value = !expanded;
  }, animationSpeed)
});

watch(() => [activeTab.value, selectedShapeStore.showUI] as const, async ([tab, showUI]) => {
  if (tab !== 'shape' || !showUI) return;
  await nextTick();

  const features = selectedShapeStore.allUpdatedShapes.map(s => s.shape);
  if (features.length === 0 && selectedShapeStore.geoJson) features.push(selectedShapeStore.geoJson.shape);
  if (features.length === 0) return;

  mapApi.value?.ensureVisible({
    features,
    rightInset: sideDrawerRef.value?.effectiveWidth ?? sideDrawerWidth.value,
  });
});

const toggleMapTable = () => {
  mapTableExpanded.value = !mapTableExpanded.value
}

function fullscreenMapTable() {
  isMapTableAnimating.value = true

  mapTableGutterSize.value = 0;
  topPanelSize.value = 0;
  bottomPanelSize.value = 101;

  mapTableSplitterRef.value?.resetState();

  setTimeout(() => {
    isMapTableAnimating.value = false
    mapApi.value?.updateSize();
  }, animationSpeed)
}

function minimizeMapTable() {

  if (bottomPanelSize.value <= 40) {

    mapTableExpanded.value = false;

  } else {

    isMapTableAnimating.value = true
    topPanelSize.value = 60;
    bottomPanelSize.value = 40;
    mapTableGutterSize.value = 4;

    mapTableSplitterRef.value?.resetState();

    setTimeout(() => {
      isMapTableAnimating.value = false
      mapApi.value?.updateSize();
    }, animationSpeed)
  }


}

function onResizeEnd(event: SplitterResizeEndEvent) {
  // event contains an array of the new sizes (e.g., [30, 70])

  if (event.sizes[0]) topPanelSize.value = event.sizes[0];
  if (event.sizes[1]) bottomPanelSize.value = event.sizes[1];
  mapApi.value?.updateSize();
};

const contextMenuDisabled = computed(() => {
  return selectedLogSorterStore.operation !== 'none';
});

const saveMapModeLoading = ref<boolean>(false);
const keyboardRotationStep = Math.PI / 18;

let hotkeyListener: ((ev: KeyboardEvent) => void) | undefined;

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

let contextMenuCoordinates: Coordinate | null = null;
let contextMenuFeature: Feature<Geometry> | null = null;
let contextMenuPlaceId: string | null = null;
let contextMenuPackageId: string | null = null;

const logSorters = ref<LogSorterResponse[]>([]);
const selectLogSorterDialogVisible = ref(false);
const selectedLogSorter = ref<LogSorterResponse>();

const menuItems = computed<MenuItem[]>(() => {
  const contextMenuTranslations = translations.value.map.contextmenu;

  return [
    {
      label: contextMenuTranslations.create.title,
      visible: () => {
        return contextMenuFeature === null && authStore.isAdmin;
      },
      items: [
        {
          label: contextMenuTranslations.create.shape,
          command() {
            Assert.definied(contextMenuCoordinates);
            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.storageArea,
          command() {
            Assert.definied(contextMenuCoordinates);

            const settings = settingStore.setting;

            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
              width: settings?.sharedClient.storageArea.logYardWidth ?? 8,
              length: settings?.sharedClient.storageArea.logYardLength ?? 20,
              sideArea: settings?.sharedClient.storageArea.logYardSideArea ?? 160,
              name: storageAreaStore.generateDefaultName(),
              storageArea: {
                type: StorageAreaType.SortedPile,
                allowedTypes: [StorageAreaType.SortedPile, StorageAreaType.UnsortedPile, StorageAreaType.CoilStack, StorageAreaType.Bulk, StorageAreaType.PackageStorage]
              }
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.logSorter,
          command() {
            Assert.definied(contextMenuCoordinates);
            selectedLogSorterStore.create({
              coordinate: contextMenuCoordinates
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.intake,
          command() {
            Assert.definied(contextMenuCoordinates);
            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
              width: 20,
              length: 20,
              storageArea: {
                type: StorageAreaType.LogSorterIntake,
                allowedTypes: [StorageAreaType.LogSorterIntake, StorageAreaType.SawLineIntake]
              }
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.productionLine,
          command() {
            Assert.definied(contextMenuCoordinates);
            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
              width: 40,
              length: 40,
              storageArea: {
                type: StorageAreaType.ProductionLine,
                allowedTypes: [StorageAreaType.ProductionLine]
              }
            });
          },
          visible() {
            return authStore.isAdmin && featureFlags.isProductionLineEnabled();
          }
        }
      ]
    },
    {
      label: contextMenuTranslations.create.insideProductionLine,
      visible: () => {
        if (!authStore.isAdmin || !featureFlags.isProductionLineEnabled()) return false;
        if (!contextMenuFeature) return false;
        const id = getStorageAreaId(contextMenuFeature);
        if (!id) return false;
        return storageAreaStore.getById(id)?.type === StorageAreaType.ProductionLine;
      },
      items: [
        {
          label: contextMenuTranslations.create.packageOutput,
          command() {
            Assert.definied(contextMenuCoordinates);
            Assert.definied(contextMenuFeature);
            const productionLineId = getStorageAreaId(contextMenuFeature);
            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
              width: 10,
              length: 10,
              storageArea: {
                type: StorageAreaType.PackageOutput,
                allowedTypes: [StorageAreaType.PackageOutput, StorageAreaType.PackageIntake],
                productionLineId
              }
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        },
        {
          label: contextMenuTranslations.create.packageIntake,
          command() {
            Assert.definied(contextMenuCoordinates);
            Assert.definied(contextMenuFeature);
            const productionLineId = getStorageAreaId(contextMenuFeature);
            selectedShapeStore.create({
              coordinate: contextMenuCoordinates,
              width: 10,
              length: 10,
              storageArea: {
                type: StorageAreaType.PackageIntake,
                allowedTypes: [StorageAreaType.PackageOutput, StorageAreaType.PackageIntake],
                productionLineId
              }
            });
          },
          visible() {
            return authStore.isAdmin;
          }
        }
      ]
    },
    {
      label: translations.value.generic.edit,
      visible: () => {
        return contextMenuFeature === null && authStore.isAdmin;
      },
      items: [
        {
          label: translations.value.generic.logSorter,
          command() {
            onEditLogSorterClick();
          },
          visible() {
            return authStore.isAdmin;
          }
        },
      ],
    },
    {
      label: contextMenuTranslations.audit,
      async command() {
        if (!contextMenuFeature) return;

        const shapeId = getShapeId(contextMenuFeature);

        if (shapeId !== undefined) {
          const response = await shapeAuditApi.execute({
            id: shapeId
          });

          if (!response.ok) {
            actionFailedAlert.show(response.details);
            return;
          }

          auditDialog.value?.show({
            audit: response.audit
          });
          return;
        }

        const vehicleId = getGeoJsonPropertyText(contextMenuFeature, 'vehicleId');

        if (vehicleId) {
          const response = await vehicleAuditApi.execute({
            id: vehicleId
          });

          if (!response.ok) {
            actionFailedAlert.show(response.details);
            return;
          }

          auditDialog.value?.show({
            audit: response.audit
          });
          return;
        }
      },
      visible() {
        if (!authStore.isSystemAdmin) return false;
        if (contextMenuFeature === null) return false;
        const shapeId = getShapeId(contextMenuFeature);
        const vehicleId = getGeoJsonPropertyText(contextMenuFeature, 'vehicleId');
        return shapeId !== undefined || vehicleId !== undefined;
      }
    },
    {
      label: contextMenuTranslations.copy,
      async command() {
        Assert.definied(contextMenuFeature);
        const shapeId = getShapeId(contextMenuFeature);
        Assert.definied(shapeId);
        selectedShapeStore.copy({
          shapeId: shapeId,
        });
      },
      visible() {
        if (!authStore.isAdmin) return false;
        if (contextMenuFeature === null) return false;
        const shapeId = getShapeId(contextMenuFeature);
        return shapeId !== undefined;
      }
    },
    {
      label: contextMenuTranslations.move,
      command() {
        if (!contextMenuFeature) return;
        const contextMenuStorageAreaId = resolveContextMenuStorageAreaId();
        if (!contextMenuStorageAreaId) return;
        const contextMenuStorageArea = storageAreaStore.getById(contextMenuStorageAreaId);
        if (!contextMenuStorageArea) return;
        const selectedStorageAreaValue = selectedStorageArea.value;
        if (!selectedStorageAreaValue) return;

        if (isCoilStorage(contextMenuStorageArea.type) && isCoilStorage(selectedStorageAreaValue.type) && moveCoilDialog.value) {
          moveCoilDialog.value.show({
            fromStorageAreaId: selectedStorageAreaValue.id,
            toStorageAreaId: contextMenuStorageArea.id
          });
        }

        if (isLogYard(contextMenuStorageArea.type) && isLogYard(selectedStorageAreaValue.type) && moveLogYardContentDialog.value) {
          moveLogYardContentDialog.value.show({
            fromStorageAreaId: selectedStorageAreaValue.id,
            toStorageAreaId: contextMenuStorageArea.id
          });
        }

        if (isBulkStorage(contextMenuStorageArea.type) && isBulkStorage(selectedStorageAreaValue.type) && moveBulkContentDialog.value) {
          moveBulkContentDialog.value.show({
            fromStorageAreaId: selectedStorageAreaValue.id,
            toStorageAreaId: contextMenuStorageArea.id
          });
        }
      },
      visible() {

        const storageAreaType = selectedStorageArea.value?.type;

        if (!storageAreaType) return false;
        if (!contextMenuFeature) return false;
        const contextMenuStorageAreaId = resolveContextMenuStorageAreaId();
        if (!contextMenuStorageAreaId) return false;
        const contextMenuStorageArea = storageAreaStore.getById(contextMenuStorageAreaId);
        if (!contextMenuStorageArea) return false;

        return (isCoilStorage(storageAreaType) && isCoilStorage(contextMenuStorageArea.type)) ||
          (isLogYard(storageAreaType) && isLogYard(contextMenuStorageArea.type)) ||
          (isBulkStorage(storageAreaType) && isBulkStorage(contextMenuStorageArea.type));
      }
    },
    {
      label: contextMenuTranslations.addInboundDeliveryTransaction,
      command() {
        const storageAreaId = resolveContextMenuStorageAreaId();
        if (!storageAreaId) return;
        onAddInboundDeliveryTransactionFromStorageArea(storageAreaId);
      },
      visible() {
        return resolveContextMenuStorageAreaId() !== undefined && featureFlags.isDeliveryTransactionEnabled() && authStore.isUser;
      }
    },
    {
      label: contextMenuTranslations.addOutboundDeliveryTransaction,
      command() {
        const storageAreaId = resolveContextMenuStorageAreaId();
        if (!storageAreaId) return;
        onAddOutboundDeliveryTransactionFromStorageArea(storageAreaId);
      },
      visible() {
        return resolveContextMenuStorageAreaId() !== undefined && featureFlags.isDeliveryTransactionEnabled() && authStore.isUser;
      }
    },
    {
      label: contextMenuTranslations.spawnPackage,
      visible: () => {
        if (!authStore.isAdmin || !featureFlags.isProductionLineEnabled()) return false;
        if (!contextMenuPlaceId) return false;

        return storageAreaStore.storageAreas.some(
          (area) => area.type === StorageAreaType.PackageOutput &&
            (area.packagePlaceData?.places ?? []).some((p) => p.id === contextMenuPlaceId)
        );
      },
      command() {
        if (!contextMenuPlaceId) return;
        const placeId = contextMenuPlaceId;
        const area = storageAreaStore.storageAreas.find(
          (a) => a.type === StorageAreaType.PackageOutput &&
            (a.packagePlaceData?.places ?? []).some((p) => p.id === placeId)
        );
        if (!area) return;
        editPackageDialog.value?.show(undefined, {
          packageStorageAreaId: area.id,
          placeId: placeId,
        });
      }
    },
    {
      label: contextMenuTranslations.copyPackage,
      visible: () => authStore.isAdmin && featureFlags.isPackagesEnabled() && contextMenuPackageId !== null,
      command() {
        if (!contextMenuPackageId) return;
        editPackageDialog.value?.showCopy(contextMenuPackageId);
      }
    }
  ]
});

function hasVisibleItems(items: MenuItem[] | undefined): boolean {
  if (!items?.length) return false

  return items.some(item => {
    // Compute actual visibility
    let visible = true
    if (typeof item.visible === 'function') {
      try {
        visible = item.visible() !== false
      } catch {
        visible = false
      }
    } else if (item.visible === false) {
      visible = false
    }

    if (item.items) {
      const hasVisibleChildren = hasVisibleItems(item.items)

      return visible && hasVisibleChildren;
    }

    return visible;
  });
}

const contextMenu = useTemplateRef('contextMenu');

function resolveContextMenuStorageAreaId() {
  if (!contextMenuFeature) {
    return undefined;
  }

  const storageAreaId = getStorageAreaId(contextMenuFeature);
  if (storageAreaId) {
    return storageAreaId;
  }

  // Preview features may not include storageAreaId while editing.
  const shapeId = getShapeId(contextMenuFeature);
  if (shapeId && selectedStorageArea.value?.id) {
    return selectedStorageArea.value.id;
  }

  return undefined;
}

function isInputTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  return target.closest('input, textarea, [contenteditable="true"], .p-inputtext, .p-select, .p-multiselect, .p-inputnumber') !== null;
}

function collectCoordinates(geometry: GeoJSON.Geometry | null): number[][] {
  if (!geometry) return [];
  switch (geometry.type) {
    case 'Point': return [geometry.coordinates];
    case 'MultiPoint':
    case 'LineString': return geometry.coordinates;
    case 'MultiLineString':
    case 'Polygon': return geometry.coordinates.flat();
    case 'MultiPolygon': return geometry.coordinates.flat(2);
    case 'GeometryCollection': return geometry.geometries.flatMap(collectCoordinates);
  }
}

async function flyToStorageArea(storageArea: StorageArea) {
  if (!selectedShapeStore.selectionEnabled) return;

  // Don't zoom when storage places are selected (in edit mode)
  if (selectedStoragePlaceIds.value.length > 0) return;
  const unsavedChangesUi = unsavedChangesDialog.value;
  const shapeUi = shapeDrawer.value;

  if (unsavedChangesUi && shapeUi && shapeUi.changed) {
    const unsavedResult = await unsavedChangesDialog.value?.show({
      saveDisabled: shapeUi.saveDisabled
    });

    switch (unsavedResult) {
      case 'Cancel':
        return;
      case 'Discard':
        break;
      case 'Save':
        await shapeUi.save();
        break;
    }
  }

  const shape = shapeStore.getByStorageAreaId(storageArea.id)[0]

  if (shape) {
    selectedShapeStore.edit({ shapeId: shape.id, multiple: false });
    mapApi.value?.flyToShape({ shape: shape, zoom: 18, duration: 200 });
  } else {
    selectedShapeIds.value.forEach(shapeId => {
      selectedShapeStore.deselect(shapeId, true);
    });
    selectedShapeStore.showUI = true;
    selectedStorageAreaStore.edit({ storageArea: storageArea, multiple: false })

  };
}

onMounted(async () => {
  const rootElement = rootElementRef.value;
  if (!rootElement) throw new Error('Map div not found!');

  const { mapSettings } = storeToRefs(settingStore);
  await refreshSettingStore.initialize();
  if (disposed) return;

  const storedSelectedMapView = localStorage.getItem("selected-map-view");

  let selectedMapView: MapSettingOptions | null = null;
  if (storedSelectedMapView) {
    try {
      selectedMapView = JSON.parse(storedSelectedMapView) as MapSettingOptions;
    } catch (ex) {
      console.log("Unvalid json string in localStorage 'selected-map-view': ", storedSelectedMapView)
    }
  }

  // Fetch MapTiler availability from the server; the key itself stays server-side.
  let satelliteEnabled = false;
  try {
    const response = await fetch('api/map/maptiler/status');
    if (disposed) return;
    if (response.ok) {
      const status = await response.json();
      satelliteEnabled = status.enabled;
    } else {
      console.error('Failed to fetch MapTiler status:', response.status);
    }
  } catch (ex) {
    console.error('Error fetching MapTiler status:', ex);
  }

  const osmSource = new XYZ({
    url: 'api/map/osm/tile/{z}/{x}/{y}',
    maxZoom: 19,
  });

  const satelliteSource = satelliteEnabled
    ? new XYZ({
      url: `api/map/maptiler-satellite/tile/{z}/{x}/{y}`,
      tileSize: 512,
      maxZoom: 22,
    })
    : null;

  const baseLayerStorageKey = 'selected-base-layer';
  const storedBaseLayer = localStorage.getItem(baseLayerStorageKey);
  currentBaseLayer.value =
    satelliteEnabled && (storedBaseLayer === 'osm' || storedBaseLayer === 'satellite')
      ? storedBaseLayer
      : 'osm';

  const tileLayer = new TileLayer({
    className: 'tile-layer',
    source: currentBaseLayer.value === 'osm' ? osmSource : satelliteSource!,
  });

  const center = selectedMapView ?
    [selectedMapView.longitude, selectedMapView.latitude] :
    [mapSettings.value.longitude, mapSettings.value.latitude];

  const zoom = selectedMapView ? selectedMapView.zoom : mapSettings.value.zoom;

  const bearing = selectedMapView ? selectedMapView.bearing : mapSettings.value.bearing;

  const view = new View({
    center: center,
    zoom: zoom,
    rotation: bearing,
    minZoom: mapSettings.value.minZoom ?? 3,
    maxZoom: 23,
    constrainRotation: false,
  });

  const map = new OLMap({
    target: rootElement,
    view: view,
    controls: [],
    layers: [tileLayer],
  });

  mapApi.value = {
    get bearing() {
      return view.getRotation();
    },
    get zoom() {
      return view.getZoom() ?? 15;
    },
    get center() {
      const center = view.getCenter();
      Assert.definied(center);
      return {
        longitude: center[0]!,
        latitude: center[1]!
      }
    },
    flyTo(options) {
      const { center, bearing, zoom, duration } = options;

      view.animate({
        center: [center.longitude, center.latitude],
        rotation: bearing,
        zoom: zoom,
        duration: duration
      });
    },
    setBearing(bearing) {
      view.setRotation(bearing);
    },
    rotateBy(delta) {
      view.setRotation(view.getRotation() + delta);
    },
    zoomBy(delta) {
      view.setZoom((view.getZoom() ?? 15) + delta);
    },
    setBearingAnimated(bearing, duration = 120) {
      view.animate({
        rotation: bearing,
        duration,
      });
    },
    rotateByAnimated(delta, duration = 120) {
      view.animate({
        rotation: view.getRotation() + delta,
        duration,
      });
    },
    zoomByAnimated(delta, duration = 120) {
      view.animate({
        zoom: (view.getZoom() ?? 15) + delta,
        duration,
      });
    },
    onBearingChanged(listener) {
      const onChange = () => listener(view.getRotation());
      view.on('change:rotation', onChange);
      return () => view.un('change:rotation', onChange);
    },
    flyToShape(options) {
      const { shape, bearing, zoom, duration } = options;
      if (!shape.shapeGeoJson) return

      const result = turfCenter(shape.shapeGeoJson as AllGeoJSON)
      const [lng, lat] = result.geometry.coordinates
      if (lng && lat) {

        view.animate({
          center: [lng, lat],
          rotation: bearing,
          zoom: zoom,
          duration: duration
        })
      }
    },
    ensureVisible(options) {
      const { features, rightInset, margin = 40, duration = 300 } = options;
      const size = map.getSize();
      if (!size) return;

      const coordinates = features.flatMap(f => collectCoordinates(f.geometry));
      if (coordinates.length === 0) return;

      const visibleRight = size[0]! - rightInset;
      const covered = coordinates.some(c => {
        const pixel = map.getPixelFromCoordinate(c);
        return pixel !== null && pixel[0]! > visibleRight;
      });
      if (!covered) return;

      // maxZoom = current zoom: keep zoom, only zoom out when the area does not fit.
      view.fit(new MultiPoint(coordinates), {
        padding: [margin, rightInset + margin, margin, margin],
        maxZoom: view.getZoom(),
        duration,
      });
    },
    updateSize() {
      map.updateSize();
    },
    padding(top, right, bottom, left) {
      view.padding = [top, right, bottom, left]
    },
    get baseLayer() {
      return currentBaseLayer.value;
    },
    get satelliteAvailable() {
      return satelliteEnabled;
    },
    setBaseLayer(layer) {
      if (layer === 'satellite' && !satelliteEnabled) return;
      if (layer === currentBaseLayer.value) return;

      currentBaseLayer.value = layer;
      tileLayer.setSource(layer === 'osm' ? osmSource : satelliteSource!);
      localStorage.setItem(baseLayerStorageKey, layer);
    },
  }
  cleanups.push(watch(mapSettings, (s) => {
    view.animate({ center: [s.longitude, s.latitude], rotation: s.bearing, zoom: s.zoom });
  }));

  previewPolygonLayer.attachListeners(map);

  // Attached before the shape layer so a package click wins over storage area selection.
  let packagesSource = null;
  if (featureFlags.isPackagesEnabled()) {
    packagesSource = packagesLayer.attach(map);
  }

  const shapeSource = shapeLayer.attach(map);
  // cleanups.push(() => shapeLayer.detach());

  const previewRectangleSource = previewRectangleLayer.attach(map);
  const previewPolygonSource = previewPolygonLayer.attach(map);
  const vehicleSource = vehiclesLayer.attach(map);

  if (featureFlags.isLogYardEnabled()) {
    previewLogsorterLayer.attach(map);
  }

  let placesSource = null;
  if(featureFlags.isPackagesEnabled()) {
      placesSource = placesLayer.attach(map);
      placesEditInteraction.attach(map);
      cleanups.push(() => placesEditInteraction.detach());
  }

  const mapElement = map.getTargetElement();

  hotkeyListener = (ev: KeyboardEvent) => {
    const key = ev.key.toLowerCase();
    const inInput = isInputTarget(ev.target);

    // Undo / redo of storage places. Works whenever a package storage area is
    // being edited, except while actively typing in a form field (so the
    // browser's native text undo still applies there).
    if ((ev.ctrlKey || ev.metaKey) && !inInput && packageStorageOperation.value !== 'none') {
      if (key === 'z' && !ev.shiftKey) {
        ev.preventDefault();
        selectedPackageStorageStore.undo();
        return;
      }
      if (key === 'y' || (key === 'z' && ev.shiftKey)) {
        ev.preventDefault();
        selectedPackageStorageStore.redo();
        return;
      }
    }

    if (inInput) return;
    if (!mapApi.value) return;

    if (key === 'q') {
      ev.preventDefault();
      mapApi.value.rotateBy(-keyboardRotationStep);
      return;
    }

    if (key === 'e') {
      ev.preventDefault();
      mapApi.value.rotateBy(keyboardRotationStep);
      return;
    }

    if (key === 'n') {
      ev.preventDefault();
      mapApi.value.setBearing(0);
    }
  };

  window.addEventListener('keydown', hotkeyListener);
  cleanups.push(() => window.removeEventListener('keydown', hotkeyListener!));

  const onContextMenu = (ev: MouseEvent) => {
    if (contextMenuDisabled.value) return;

    const coordinate = getRenderedEventCoordinate(map, ev);
    if (!coordinate) return;

    contextMenuCoordinates = {
      longitude: coordinate[0]!,
      latitude: coordinate[1]!
    };

    const shapeFeatures = shapeSource.getFeaturesAtCoordinate(coordinate);
    const vehicleFeatures = vehicleSource.getFeaturesAtCoordinate(coordinate);
    const previewRectangleFeatures = previewRectangleSource.getFeaturesAtCoordinate(coordinate);
    const previewPolygonFeatures = previewPolygonSource.getFeaturesAtCoordinate(coordinate);

    const features = [
      ...shapeFeatures,
      ...vehicleFeatures,
      ...previewRectangleFeatures,
      ...previewPolygonFeatures,
    ].sort((a, b) => (b.get('zIndex') ?? 0) - (a.get('zIndex') ?? 0));

    if (features.length > 0) {
      const storageAreaFeature = features.find((feature) => getStorageAreaId(feature) !== undefined);
      const shapeFeature = features.find((feature) => getShapeId(feature) !== undefined);
      contextMenuFeature = storageAreaFeature ?? shapeFeature ?? features[0]!;
    } else {
      contextMenuFeature = null;
    }

    const placeFeatures = placesSource ? placesSource.getFeaturesAtCoordinate(coordinate) : [];
    contextMenuPlaceId = placeFeatures.length > 0 ? (placeFeatures[0]!.get('placeId') ?? null) : null;

    const packageFeatures = packagesSource ? packagesSource.getFeaturesAtCoordinate(coordinate) : [];
    contextMenuPackageId = packageFeatures.length > 0 ? (packageFeatures[0]!.get('packageId') ?? null) : null;

    if (!hasVisibleItems(menuItems.value)) {
      return;
    }

    contextMenu.value?.show(ev);
  };
  mapElement.addEventListener('contextmenu', onContextMenu);
  cleanups.push(() => mapElement.removeEventListener('contextmenu', onContextMenu));

  setupDragBox(map, shapeSource);
});

onBeforeUnmount(() => {
  disposed = true;

  animationTimers.forEach(clearTimeout);
  animationTimers = [];

  cleanups.splice(0).reverse().forEach((fn) => {
    try { fn(); } catch (e) { console.error(e); }
  });
  unByKey(olKeys.splice(0));

  if (map) {
    map.getInteractions().clear();

    // layers first, then the map (map.dispose() does NOT dispose layers)
    map.getLayers().getArray().slice().forEach((layer) => {
      (layer as any).getSource?.()?.clear?.(); // vector sources
      layer.dispose();
    });
    map.getLayers().clear();

    map.setTarget(undefined);
    map.dispose();
    map = undefined;
  }

  mapApi.value = undefined;
});

setupVehicleLayer();
setupShapeLayer();

function setupVehicleLayer() {

  onMounted(async () => {
    await refreshVehicleStore.initialize();
  });

  newPosition.on((position) => {
    const timestamp = new Date(position.clientTimestampUTC);

    vehiclesLayer.mergePosition(position.vehicleId, {
      latitude: position.latitude,
      longitude: position.longitude,
      timestamp: timestamp.getTime(),
      heading: position.heading,
      speed: position.speed
    });
  });
}

function setupShapeLayer() {

  shapeLayer.addClickListener(async (ev) => {
    if (!selectedShapeStore.selectionEnabled) return;

    const shapeId = ev.shapeId;
    const multiple = ev.multiple;

    // Clicking the shape that is already selected (e.g. to pick a storage place
    // inside the storage area currently being edited) must not prompt for
    // unsaved changes, since the edited entity is not changing.
    if (shapeId !== null && selectedShapeStore.selectedShapeIds.indexOf(shapeId) !== -1) {
      if (multiple) {
        selectedShapeStore.deselect(shapeId);
      }

      return;
    }

    const unsavedChangesUi = unsavedChangesDialog.value;
    const shapeUi = shapeDrawer.value;

    if (shapeId !== null) {

      if (unsavedChangesUi && shapeUi && shapeUi.changed) {
        const unsavedResult = await unsavedChangesDialog.value?.show({
          saveDisabled: shapeUi.saveDisabled
        });

        switch (unsavedResult) {
          case 'Cancel':
            return;
          case 'Discard':
            selectedShapeStore.deselect(shapeId);
            break;
          case 'Save':
            await shapeUi.save();
            break;
        }
      }

      if (selectedShapeStore.selectedShapeIds.indexOf(shapeId) !== -1) {
        if (multiple) {
          selectedShapeStore.deselect(shapeId);
        }

        return;
      };
      selectedShapeStore.edit({ shapeId, multiple });
    } else {
      selectedShapeStore.deselect(null);
    }
  });

  const { storageAreaIds } = storeToRefs(selectedLogSorterStore);

  watch(storageAreaIds, (newStorageAreaIds) => {
    shapeLayer.hideStorageArea((ids) => {
      ids.clear();
      for (const id of newStorageAreaIds) {
        ids.add(id);
      }
    })
  });

  const { selectedShapeIds } = storeToRefs(selectedShapeStore);

  watch(selectedShapeIds, (newSelectedShapeIds) => {
    shapeLayer.hideShapes((ids) => {
      ids.clear();
      for (const id of newSelectedShapeIds) {
        ids.add(id);
      }
    });
  });

  onMounted(async () => {
    useRefreshState();
  });
}

function setupDragBox(map: OLMap, shapeSource: VectorSource) {
  const dragBox = new DragBox({
    condition: platformModifierKeyOnly,
  });

  map.addInteraction(dragBox);

  olKeys.push(dragBox.on('boxend', async () => {
    const boxExtent = dragBox.getGeometry().getExtent();
    if (!boxExtent || boxExtent.length !== 4) return;

    const mapProjection = map.getView().getProjection();
    const worldExtent = mapProjection.getExtent();
    const worldWidth = getWidth(worldExtent);
    const startWorld = Math.floor((boxExtent[0]! - worldExtent[0]!) / worldWidth);
    const endWorld = Math.floor((boxExtent[2]! - worldExtent[0]!) / worldWidth);

    const rotation = map.getView().getRotation();
    const oblique = rotation % (Math.PI / 2) !== 0;

    const selectedFeatures: typeof shapeSource extends VectorSource<infer F> ? F[] : any[] = [];

    for (let world = startWorld; world <= endWorld; ++world) {
      const left = Math.max(boxExtent[0]! - world * worldWidth, worldExtent[0]!);
      const right = Math.min(boxExtent[2]! - world * worldWidth, worldExtent[2]!);
      const extent = [left, boxExtent[1]!, right, boxExtent[3]!];

      // shapeSource geometries are in EPSG:4326, the map view extent is in
      // the map's projection (likely EPSG:3857) — reproject before querying
      const sourceExtent = transformExtent(extent, mapProjection, 'EPSG:4326');

      let boxFeatures = shapeSource
        .getFeaturesInExtent(sourceExtent)
        .filter((feature) => feature!.getGeometry()!.intersectsExtent(sourceExtent));

      if (oblique) {
        // rotate the box geometry itself (in map projection), then
        // transform each candidate feature's geometry into map
        // projection to compare apples to apples
        const anchor = [0, 0];
        const rotatedBox = dragBox.getGeometry().clone();
        rotatedBox.translate(-world * worldWidth, 0);
        rotatedBox.rotate(-rotation, anchor);
        const oExtent = rotatedBox.getExtent();

        boxFeatures = boxFeatures.filter((feature) => {
          const geometry = feature!.getGeometry()!.clone();
          geometry.transform('EPSG:4326', mapProjection);
          geometry.rotate(-rotation, anchor);
          return geometry.intersectsExtent(oExtent);
        });
      }

      selectedFeatures.push(...boxFeatures);
    }

    if (selectedFeatures.length > 0) {
      const shapeIds = selectedFeatures
        .map((f) => f.getId())
        .filter((id): id is string | number => id != null)
        .map((id) => id.toString());

      await selectedShapeStore.multiEdit(shapeIds);
    }
  }));
}

function onSearchClick() {
  Assert.definied(searchLogYardDialogRef.value);
  searchLogYardDialogRef.value.visible = true;
}

function onPackageClick(packageId: string | null) {
  if (packageId) packageDetailsStore.open(packageId);
  else packageDetailsStore.close();
}

function onAddInboundDeliveryTransactionClick() {
  addInboundDeliveryTransactionDialog.value?.show();
}

function onAddOutboundDeliveryTransactionClick() {
  addOutboundDeliveryTransactionDialog.value?.show();
}

function onAddInboundDeliveryTransactionFromStorageArea(storageAreaId: string) {
  addInboundDeliveryTransactionDialog.value?.show({
    storageAreaId,
    filterMode: 'matchingStorageArea',
  });
}

function onAddOutboundDeliveryTransactionFromStorageArea(storageAreaId: string) {
  addOutboundDeliveryTransactionDialog.value?.show({
    storageAreaId,
    filterMode: 'matchingStorageArea',
  });
}

async function onMoveTimberFromLogSorterBins() {
  const response = await moveTimberFromLogSorterBinsApi.execute();
  if (!response.ok) {
    actionFailedAlert.show(response.details);
  }
}

function executeSearch() {
  Assert.definied(searchLogYardDialogRef.value);
  Assert.definied(searchLogYardDialogRef.value.selectedProduct);

  const foundIds = useSearchProduct(searchLogYardDialogRef.value.selectedProduct.id, storageAreaStore.storageAreas);
  shapeLayer.searchStorageAreas((ids) => {
    ids.clear();
    foundIds.forEach(id => {
      ids.add(id);
    });
  });
}

function clearSearch() {
  shapeLayer.clearSearchStorageAreas();
}

async function onEditLogSorterClick() {

  const response = await getLogSorters.execute();

  if (response.ok) {
    logSorters.value = response.logSorter

    if (logSorters.value.length == 0) return;
    if (logSorters.value.length == 1) {

      const editLogSorter: EditLogSorter = {
        logSorter: logSorters.value[0]!
      }
      selectedLogSorterStore.edit(editLogSorter);
    } else if (logSorters.value.length > 1) {
      selectLogSorterDialogVisible.value = true;
    }
  }
};

function selectEditLogSorter() {
  if (selectedLogSorter.value) {
    const editLogSorter: EditLogSorter = {
      logSorter: selectedLogSorter.value
    }
    selectedLogSorterStore.edit(editLogSorter);
    selectLogSorterDialogVisible.value = false;
  }
}

const activeProductName = computed(() =>
  searchLogYardDialogRef.value?.searchActive
    ? searchLogYardDialogRef.value.selectedProduct?.name
    : undefined
);

</script>

<template>
  <MainLayout>
    <template #default>
      <Splitter style="height: 100%" layout="vertical" ref="mapTableSplitter"
        :class="{ 'splitter-animate': isMapTableAnimating }"
        @resizeend="(e: SplitterResizeEndEvent) => onResizeEnd(e)" :gutterSize="mapTableGutterSize">
        <SplitterPanel class="splitter-panel map-panel" :size="topPanelSize">
          <div class="map-view" ref="map-view" :class="mapDarkMode ? 'map-darkmode' : ''">
            <div class="table-button">
              <Button v-if="mapTableBtnVisible" icon="pi pi-angle-up" rounded severity="secondary"
                aria-label="Toggle table" @click="toggleMapTable" />
            </div>
            <div class="map-control-buttons">
              <MapRotationControls v-if="mapApi" :map-api="mapApi"></MapRotationControls>
              <BaseLayerSelect v-if="mapApi?.satelliteAvailable" :map-api="mapApi"></BaseLayerSelect>
            </div>
          </div>
          <PackageDetailsDrawer v-if="featureFlags.isPackagesEnabled()" :z-index="1002"></PackageDetailsDrawer>
          <MapSideDrawer ref="side-drawer" v-model:width="sideDrawerWidth" :open="isSideDrawerOpen"
            :min-width="isLogSorterPanelOpen ? 700 : 300" :z-index="1001">
            <ShapeDrawer ref="shape-drawer"></ShapeDrawer>
            <LogSorterDrawer v-if="featureFlags.isLogYardEnabled()" ref="log-sorter-drawer"></LogSorterDrawer>
          </MapSideDrawer>
        </SplitterPanel>
        <SplitterPanel class="splitter-panel" :size="bottomPanelSize">
          <MapTable @fly-to-storage-area="(storageArea) => flyToStorageArea(storageArea)"
            @fullscreen="fullscreenMapTable()" @minimize="minimizeMapTable()"></MapTable>
        </SplitterPanel>
      </Splitter>
    </template>
    <template #header>
      <Button v-if="featureFlags.isFeatureEnabled('MoveTimberFromLogSorterBins')"
        :label="translations.moves.moveTimberFromLogSorterBins" :loading="moveTimberFromLogSorterBinsApi.loading.value"
        @click="onMoveTimberFromLogSorterBins"></Button>
      <Button v-if="featureFlags.isDeliveryTransactionEnabled()"
        :label="translations.inboundDelivery.transactionDialog.openButton"
        @click="onAddInboundDeliveryTransactionClick">
      </Button>
      <Button v-if="featureFlags.isDeliveryTransactionEnabled()"
        :label="translations.outboundDelivery.transactionDialog.openButton"
        @click="onAddOutboundDeliveryTransactionClick">
      </Button>
      <SearchFilterButton :active-product="activeProductName"
        :loading="saveMapModeLoading" @open="onSearchClick" @clear="searchLogYardDialogRef?.clear()" />
        <UndoButton></UndoButton>
      <MapView v-if="mapApi" :map-api="mapApi"></MapView>
      <MapStyleSelect></MapStyleSelect>
    </template>
  </MainLayout>
  <ContextMenu ref="contextMenu" :model="menuItems"></ContextMenu>
  <LogYardEditContentDialog v-if="featureFlags.isLogYardEnabled()"></LogYardEditContentDialog>
  <BulkEditContentDialog v-if="featureFlags.isBulkEnabled()"></BulkEditContentDialog>
  <UnsavedChangesDialog ref="unsaved-changes"></UnsavedChangesDialog>
  <AuditDialog ref="audit-dialog"></AuditDialog>
  <MoveCoilDialog v-if="featureFlags.isCoilEnabled()" ref="move-coil-dialog"></MoveCoilDialog>
  <EditPackageDialog v-if="featureFlags.isPackagesEnabled()" ref="edit-package-dialog"
    @saved="refreshPackageStore.refresh()"></EditPackageDialog>
  <MoveLogYardContentDialog v-if="featureFlags.isLogYardEnabled()" ref="move-log-yard-content-dialog">
  </MoveLogYardContentDialog>
  <MoveBulkContentDialog v-if="featureFlags.isBulkEnabled()" ref="move-bulk-content-dialog"></MoveBulkContentDialog>
  <AddInboundDeliveryTransactionDialog ref="add-inbound-delivery-transaction-dialog">
  </AddInboundDeliveryTransactionDialog>
  <AddOutboundDeliveryTransactionDialog ref="add-outbound-delivery-transaction-dialog">
  </AddOutboundDeliveryTransactionDialog>
  <SearchLogYardDialog ref="search-log-yard-dialog" @execute-product-search="executeSearch"
    @clear-product-search="clearSearch"></SearchLogYardDialog>
  <Dialog v-model:visible="selectLogSorterDialogVisible" modal :header="translations.dialogs.selectLogSorter.title"
    :style="{ width: '25rem' }">
    <label for="selectLogSorter">{{ translations.generic.logSorter }}</label>
    <Select id="selectLogSorter" v-model="selectedLogSorter" :options="logSorters" optionLabel="name" fluid></Select>
    <div class="flex justify-end gap-2 pt-3">
      <Button type="button" :label="translations.generic.edit" @click="selectEditLogSorter"></Button>
      <Button type="button" :label="translations.generic.abort" severity="secondary"
        @click="selectLogSorterDialogVisible = false"></Button>
    </div>
  </Dialog>
</template>

<style scoped>
.map-view {
  position: relative;
  height: 100%;
  flex: 1;
  min-width: 0;
}

/* Positioning context for the package details drawer that overlays the map. */
.map-panel {
  position: relative;
  overflow: hidden;
}

.table-button {
  position: absolute;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  z-index: 1000;
  pointer-events: auto;
}

.map-control-buttons {
  position: absolute;
  left: 1rem;
  top: 1rem;
  z-index: 1000;
  pointer-events: auto;

  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.splitter-animate .splitter-panel {
  transition: flex-basis 300ms ease, max-height 300ms ease;
}
</style>

<style>
.map-darkmode .tile-layer {
  filter: invert(90%) hue-rotate(180deg) brightness(80%) contrast(90%);
}
</style>
